import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// @dynamic-i18n-keys
// Dynamic t() at line 124 (common.productCategory.${row.productType}):
//   common.productCategory.exclusive
//   common.productCategory.exclusive_resale
//   common.productCategory.pcp
//   common.productCategory.premium_gallery
//   common.productCategory.commission
//   common.productCategory.originals
//   common.productCategory.tier
import { useSelector } from 'react-redux';
import { PapiStudioSellerStatsInit } from '@wix/da-papi-types';
import Dumbo from '@wix/da-shared-react/pkg/DeviationViews/Dumbo';
import { getCoreLevel } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import ReportsSection from '../ReportsSection';
import ReportsTable from '../ReportsTable';
import CoreUpsellRow from '../ReportsTable/CoreUpsellRow';
import DeviationLink from '@wix/da-shared-react/pkg/Duperbrowse/DeviationLink';
import FolderLink from '@wix/da-shared-react/pkg/FolderCard/_partials/FolderLink';
import { ProductType } from '../../products/types';
import { TimePeriod } from '../../../types/reports';

import s from './TopSellingProductsSection.scss';

export interface Props {
  className?: string;
  data?: PapiStudioSellerStatsInit['topSellingProducts'];
}

const TopSellingProductsSection: FC<Props> = ({ data, className }) => {
  const { t } = useTranslation();
  const userCoreLevel = useSelector(getCoreLevel);
  const hasCoreProPlus =
    userCoreLevel === 'core_pro_plus' || userCoreLevel === 'core_max';
  const columns = useMemo(() => {
    return [
      {
        key: 'title',
        label: t('studio.reports.section.topSellers.table.header.title'),
      },
      {
        key: 'productType',
        label: t('studio.reports.section.topSellers.table.header.productType'),
      },
      {
        key: 'totalSales',
        label: t('studio.reports.section.topSellers.table.header.totalSales'),
      },
      {
        key: 'salesVolume',
        label: t('studio.reports.section.topSellers.table.header.salesVolume'),
      },
    ];
  }, [t]);

  if (!data) {
    return null;
  }

  const renderThumb = ({ productType, premiumGallery, deviation }) => {
    const dumbo = (
      <Dumbo
        deviation={
          productType === ProductType.PremiumGallery
            ? premiumGallery?.thumb
            : deviation
        }
        width={64}
        height={64}
      />
    );
    return productType === ProductType.PremiumGallery
      ? premiumGallery && (
          <FolderLink folder={premiumGallery}>{dumbo}</FolderLink>
        )
      : deviation && (
          <DeviationLink deviation={deviation} canLinkToStash>
            {dumbo}
          </DeviationLink>
        );
  };

  return (
    <ReportsSection
      reportType="topSellingProducts"
      className={className}
      title={t('studio.reports.section.topSellingProducts.title')}
      description={t('studio.reports.section.topSellingProducts.description', {
        postProcess: ['reactPostprocessor'],
        learnMoreLink: (
          <a
            className="text-link-level-2-tertiary"
            href={t(
              'studio.reports.section.topSellingProducts.description.learnMoreLink.url'
            )}
          >
            {t(
              'studio.reports.section.topSellingProducts.description.learnMoreLink.text'
            )}
          </a>
        ),
      })}
      data={data}
      dataFilter={dataToFilter =>
        /* show top 50 products for core members */
        dataToFilter?.slice(0, hasCoreProPlus ? 50 : 3)
      }
      renderTable={(
        filteredData: any[],
        timePeriod: TimePeriod,
        unfilteredData: any[]
      ) => (
        <ReportsTable
          tableKey={`top-sellers-${timePeriod}`}
          columns={columns}
          data={filteredData}
          initialVisibleCount={hasCoreProPlus ? 5 : 3}
          renderRow={row => (
            <>
              <td>
                <div className={s['info']}>
                  {renderThumb(row)}
                  <div className={s['title']}>
                    {row.productType === ProductType.PremiumGallery
                      ? row.premiumGallery?.name
                      : row.deviation?.title}
                  </div>
                </div>
              </td>
              <td>{t(`common.productCategory.${row.productType}`)}</td>
              <td>{row.totalSales}</td>
              <td>{formatDollars(row.salesVolume)}</td>
            </>
          )}
          renderFooter={() =>
            unfilteredData.length > 3 && !hasCoreProPlus && <CoreUpsellRow />
          }
        />
      )}
    />
  );
};

export default TopSellingProductsSection;
